import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, Rocket, FileText, Sparkles } from 'lucide-react';
import { useTranslation } from '@/hooks/useTypedTranslation';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { PageLayout } from '@/components/PageLayout';
import { StructuredData, generateBreadcrumbs } from '@/components/StructuredData';

// Identity card configuration
const identityConfigs = [
  {
    id: 'trauma',
    icon: Heart,
    gradient: 'from-rose-500/20 via-pink-500/10 to-transparent',
    accentColor: 'text-rose-400',
    glowColor: 'bg-rose-500/10',
    borderColor: 'border-rose-500/30',
    link: '/about/trauma'
  },
  {
    id: 'entrepreneur',
    icon: Rocket,
    gradient: 'from-blue-500/20 via-cyan-500/10 to-transparent',
    accentColor: 'text-blue-400',
    glowColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    link: '/about/entrepreneur'
  },
  {
    id: 'resume',
    icon: FileText,
    gradient: 'from-amber-500/20 via-orange-500/10 to-transparent',
    accentColor: 'text-amber-400',
    glowColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    link: '/about/resume'
  }
];

export default function About() {
  const { t, i18n } = useTranslation();
  const { getLocalizedPath } = useLocalizedPath();
  const lang = i18n.language as 'en' | 'zh';

  const breadcrumbs = generateBreadcrumbs([
    { label: lang === 'zh' ? '关于我' : 'About', path: '/about' }
  ], lang);

  return (
    <PageLayout>
      <StructuredData type="profile" breadcrumbs={breadcrumbs} />
      {/* Hero Section */}
      <div className="relative min-h-[50vh] bg-black overflow-hidden">
        {/* 背景渐变 */}
        <div className="absolute inset-0 bg-gradient-to-br from-rose-500/10 via-transparent to-blue-500/10" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-accent-purple/5 via-transparent to-transparent" />
        
        {/* 装饰性光效 */}
        <div className="absolute top-1/3 left-1/3 w-80 h-80 bg-rose-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/3 right-1/3 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* 内容 */}
        <div className="relative z-10 container mx-auto px-6 pt-32 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 glass-effect px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-rose-400" />
              <span className="text-sm font-medium text-white/80">About Me</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 text-white leading-tight">
              {t.about.title}
            </h1>
            
            <p className="text-lg sm:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed">
              {t.about.subtitle}
            </p>
          </motion.div>
        </div>
        
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
      </div>

      {/* Identity Cards */}
      <div className="bg-background py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {identityConfigs.map((config, index) => {
              const Icon = config.icon;
              const identityContent =
                config.id === 'trauma'
                  ? t.about.identities.trauma
                  : config.id === 'entrepreneur'
                  ? t.about.identities.entrepreneur
                  : t.about.identities.resume;
              
              return (
                <motion.div
                  key={config.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                >
                  <Link
                    to={getLocalizedPath(config.link)}
                    className={`group relative block p-8 rounded-2xl border ${config.borderColor} bg-card hover:shadow-2xl transition-all duration-500 overflow-hidden`}
                  >
                    {/* 背景渐变 */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${config.gradient} opacity-50 group-hover:opacity-100 transition-opacity duration-500`} />
                    
                    {/* 装饰光效 */}
                    <div className={`absolute -top-20 -right-20 w-40 h-40 ${config.glowColor} rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    
                    <div className="relative z-10">
                      <div className={`mb-6 p-3 ${config.glowColor} rounded-xl inline-block`}>
                        <Icon className={`w-8 h-8 ${config.accentColor}`} aria-hidden="true" />
                      </div>
                      
                      <h2 className="text-2xl font-bold mb-2 group-hover:text-primary transition-colors">
                        {identityContent.title}
                      </h2>
                      
                      <p className={`text-sm ${config.accentColor} mb-4`}>
                        {identityContent.subtitle}
                      </p>
                      
                      <p className="text-muted-foreground leading-relaxed mb-6">
                        {identityContent.description}
                      </p>
                      
                      <div className={`flex items-center gap-2 ${config.accentColor} opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-0 group-hover:translate-x-2`}>
                        <span className="text-sm font-medium">了解更多</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {/* Quote */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20 text-center"
          >
            <div className="relative inline-block">
              <div className="absolute -inset-4 bg-gradient-to-r from-rose-500/10 via-purple-500/10 to-blue-500/10 rounded-2xl blur-xl" />
              <blockquote className="relative text-2xl md:text-3xl font-light text-muted-foreground italic max-w-3xl mx-auto px-8 py-6">
                "{t.about.quote}"
              </blockquote>
            </div>
          </motion.div>
        </div>
      </div>
    </PageLayout>
  );
}
